import { OverlayConfig } from '@angular/cdk/overlay';
import { ComponentRef, inject, Injectable, ViewContainerRef } from '@angular/core';
import type { RmaOverlayService } from '@rma/generic/ui/overlay';
import { LazyInject } from '@rma/generic/util/lazy-inject';
import { interval, take } from 'rxjs';
import { CalloutComponent } from './callout.component';
import { CalloutConfig } from './callout-config';

export interface CalloutServiceConfig {
  callout: CalloutConfig;
  overlay: OverlayConfig;
}

@Injectable({
  providedIn: 'root',
})
export class CalloutService {
  private readonly lazyInjector = inject(LazyInject);

  public async getCalloutComponent(config: CalloutServiceConfig, vcr: ViewContainerRef | null) {
    const overlayService = await this.lazyInjector.get<RmaOverlayService>(() =>
      import('@rma/generic/ui/overlay').then((m) => m.RmaOverlayService),
    );

    const overlayConfig: OverlayConfig = {
      scrollStrategy: overlayService.overlay.scrollStrategies.reposition(),
      positionStrategy: overlayService.overlay.position().global().centerHorizontally().top('64px'),
      ...config.overlay,
    };

    const { overlayRef, contentRef } = overlayService.open(vcr, CalloutComponent, overlayConfig);

    overlayRef?.backdropClick().subscribe(() => overlayRef.detach());

    const calloutConfig = config.callout;

    const component = contentRef as ComponentRef<CalloutComponent>;
    component.setInput('calloutType', calloutConfig.calloutType);
    component.setInput('message', calloutConfig.message);
    component.setInput('isHtml', calloutConfig.isHtml);
    component.setInput('duration', calloutConfig.duration);
    component.setInput('dismissible', calloutConfig.dismissible);

    component.instance.dismiss.pipe(take(1)).subscribe(() => overlayRef.detach());

    if (calloutConfig.duration) {
      interval(calloutConfig.duration)
        .pipe(take(1))
        .subscribe(() => overlayRef.detach());
    }

    return overlayRef;
  }
}
